/**
 * DiscussContextSidebar — left-column readout of what the debug agent is
 * being shown.
 *
 * Purely informational: lists the execution's steps and, for each
 * `invoke_agent` step, the count of transcript turns loaded into context.
 * Operators glance at this to confirm "the agent can see the email-writer
 * session" before asking a question that depends on it.
 *
 * No interactivity required. If we later need a per-session include/exclude
 * toggle (out of scope per design Q3), this is the component that grows it.
 */

import type { CSSProperties } from 'react'
import { AlertCircle, CheckCircle2, Loader2, FileText } from 'lucide-react'

import type { ExecutionDiscussContext } from '@/features/session/types'
import type {
  DiscussContextStatus,
} from '@/features/queue/hooks/useDiscussContext'

export interface DiscussContextSidebarProps {
  status: DiscussContextStatus
  context: ExecutionDiscussContext | null
  /** Per-session error map from `useDiscussContext`. */
  perSessionErrors: Map<string, string>
  /** Execution-level fetch error from `useDiscussContext`. */
  executionError: string | null
}

export function DiscussContextSidebar({
  status,
  context,
  perSessionErrors,
  executionError,
}: DiscussContextSidebarProps) {
  return (
    <aside style={styles.sidebar} data-testid="discuss-context-sidebar">
      <header style={styles.header}>
        <h3 style={styles.title}>Context</h3>
        <StatusPill status={status} executionError={executionError} />
      </header>

      {executionError && (
        <div style={styles.errorBlock} role="alert">
          <AlertCircle size={14} />
          <span>Failed to load execution: {executionError}</span>
        </div>
      )}

      {!context && !executionError && (
        <p style={styles.loadingText}>Loading execution and transcripts…</p>
      )}

      {context && (
        <>
          <section style={styles.section}>
            <h4 style={styles.sectionTitle}>Execution</h4>
            <dl style={styles.metaList}>
              <MetaRow label="ID" value={context.executionId} mono />
              <MetaRow label="Status" value={context.executionSnapshot.status} />
              {context.automationId && (
                <MetaRow label="Automation" value={context.automationId} mono />
              )}
              <MetaRow
                label="Steps"
                value={String(context.executionSnapshot.steps.length)}
              />
              {context.summary && (
                <MetaRow label="Summary" value="loaded" />
              )}
            </dl>
          </section>

          <section style={styles.section}>
            <h4 style={styles.sectionTitle}>
              Agent sessions ({context.sessionTranscripts.length})
            </h4>
            {context.sessionTranscripts.length === 0 ? (
              <p style={styles.dimText}>
                No agent sessions in this execution. The debug agent will
                still see step inputs / outputs.
              </p>
            ) : (
              <ul style={styles.sessionList}>
                {context.sessionTranscripts.map((t) => {
                  const err = perSessionErrors.get(t.sessionId)
                  return (
                    <li key={t.sessionId} style={styles.sessionItem}>
                      <div style={styles.sessionHeader}>
                        <span style={styles.sessionAgent}>
                          {t.agentName ?? t.agentId ?? 'Unknown agent'}
                        </span>
                        {err ? (
                          <span style={styles.sessionStatusError}>
                            <AlertCircle size={12} /> error
                          </span>
                        ) : t.truncated ? (
                          <span style={styles.sessionStatusWarn}>
                            <FileText size={12} /> truncated
                          </span>
                        ) : (
                          <span style={styles.sessionStatusOk}>
                            <CheckCircle2 size={12} /> loaded
                          </span>
                        )}
                      </div>
                      <div style={styles.sessionMeta}>
                        Step {t.stepIndex}: {t.stepName}
                      </div>
                      <div style={styles.sessionMetaMono}>
                        {t.sessionId} · {t.turns.length}{' '}
                        turn{t.turns.length === 1 ? '' : 's'}
                      </div>
                      {err && (
                        <div style={styles.sessionErrorText}>{err}</div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <section style={styles.section}>
            <h4 style={styles.sectionTitle}>Steps</h4>
            <ol style={styles.stepList}>
              {context.executionSnapshot.steps.map((step) => (
                <li key={step.index} style={styles.stepItem}>
                  <span style={styles.stepName}>
                    {step.index}. {step.name}
                  </span>
                  <span
                    style={{
                      ...styles.stepStatus,
                      color: stepStatusColor(step.status),
                    }}
                  >
                    {step.status}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </>
      )}
    </aside>
  )
}

function StatusPill({
  status,
  executionError,
}: {
  status: DiscussContextStatus
  executionError: string | null
}) {
  if (executionError || status === 'error') {
    return (
      <span style={{ ...styles.statusPill, color: 'var(--error)' }}>
        <AlertCircle size={12} /> error
      </span>
    )
  }
  if (status === 'ready') {
    return (
      <span style={{ ...styles.statusPill, color: 'var(--success)' }}>
        <CheckCircle2 size={12} /> ready
      </span>
    )
  }
  if (status === 'partial') {
    return (
      <span style={{ ...styles.statusPill, color: 'var(--warning)' }}>
        <Loader2 size={12} className="animate-spin" /> partial
      </span>
    )
  }
  return (
    <span style={{ ...styles.statusPill, color: 'var(--text-muted)' }}>
      <Loader2 size={12} className="animate-spin" /> loading
    </span>
  )
}

function MetaRow({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div style={styles.metaRow}>
      <dt style={styles.metaLabel}>{label}</dt>
      <dd style={{ ...styles.metaValue, ...(mono ? styles.mono : {}) }}>
        {value}
      </dd>
    </div>
  )
}

function stepStatusColor(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'var(--success)'
    case 'failed':
      return 'var(--error)'
    case 'running':
      return 'var(--accent-primary)'
    case 'skipped':
    case 'pending':
    default:
      return 'var(--text-muted)'
  }
}

const styles: Record<string, CSSProperties> = {
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px 18px',
    overflowY: 'auto',
    background: 'var(--bg-secondary)',
    borderRight: '1px solid var(--border-subtle)',
    minWidth: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  title: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    fontWeight: 500,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  metaList: {
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  metaRow: {
    display: 'grid',
    gridTemplateColumns: '76px 1fr',
    gap: '8px',
    fontSize: '12px',
    lineHeight: 1.4,
  },
  metaLabel: {
    margin: 0,
    color: 'var(--text-muted)',
  },
  metaValue: {
    margin: 0,
    color: 'var(--text-primary)',
    wordBreak: 'break-word',
  },
  mono: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '11.5px',
  },
  sessionList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  sessionItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    padding: '8px 10px',
    background: 'var(--bg-tertiary)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
  },
  sessionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  sessionAgent: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  sessionStatusOk: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '10.5px',
    color: 'var(--success)',
  },
  sessionStatusWarn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '10.5px',
    color: 'var(--warning)',
  },
  sessionStatusError: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '10.5px',
    color: 'var(--error)',
  },
  sessionMeta: {
    fontSize: '11.5px',
    color: 'var(--text-secondary)',
  },
  sessionMetaMono: {
    fontSize: '10.5px',
    color: 'var(--text-muted)',
    fontFamily: "'JetBrains Mono', monospace",
    wordBreak: 'break-all',
  },
  sessionErrorText: {
    fontSize: '11px',
    color: 'var(--error)',
    marginTop: '2px',
  },
  stepList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  stepItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    fontSize: '12px',
  },
  stepName: {
    color: 'var(--text-primary)',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  stepStatus: {
    fontSize: '11px',
    fontWeight: 500,
    flexShrink: 0,
  },
  errorBlock: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    padding: '8px 10px',
    background: 'rgba(239, 68, 68, 0.08)',
    border: '1px solid rgba(239, 68, 68, 0.25)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--error)',
    fontSize: '12px',
  },
  loadingText: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  dimText: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-muted)',
    lineHeight: 1.45,
  },
}

export default DiscussContextSidebar
